import type { ContextNote, ContextNoteHandlers, NextModelTarget } from '~/features/my-wallet/types/context-note'
import type { WalletPaymentModel } from '~/store-v2/my-wallet/useWalletPaymentModel'
import { isWalletBlocked } from '~/features/my-wallet/utils/my-wallet'

interface ContextNoteSources {
  summary: Pick<WalletPaymentModel['summary'], 'state' | 'is_blocked' | 'disabled_model' | 'disabled_reason' | 'current_model'>
  hasCommercialException: boolean
  hasNoCompletions: boolean
  nextModel: NextModelTarget | null
}

export function toNextModelTarget(model: string | null | undefined, projectsNeeded: number | null | undefined): NextModelTarget | null {
  return model ? { model, projectsNeeded: projectsNeeded ?? null } : null
}

/**
 * Precedence order: the first match wins. "keep" only applies when there is no next model;
 * a next model without a project count shows no note.
 */
export function resolveContextNote({ summary, hasCommercialException, hasNoCompletions, nextModel }: ContextNoteSources): ContextNote | null {
  if (isWalletBlocked(summary))
    return { kind: 'blocked' }
  if (hasCommercialException)
    return { kind: 'commercial' }
  if (summary.state === 'MODEL_DISABLED' && summary.disabled_model)
    return { kind: 'disabled', model: summary.disabled_model, reason: summary.disabled_reason ?? null }
  if (hasNoCompletions && summary.current_model)
    return { kind: 'no_completions', model: summary.current_model }
  if (!nextModel)
    return { kind: 'keep' }
  if (nextModel.projectsNeeded === null)
    return null
  return { kind: 'next', count: nextModel.projectsNeeded, model: nextModel.model }
}

export function matchContextNote<TResult>(contextNote: ContextNote, handlers: ContextNoteHandlers<TResult>): TResult {
  // TS cannot correlate handlers[kind] with the note kind; ContextNoteHandlers guarantees the pairing.
  const handler = handlers[contextNote.kind] as (note: ContextNote) => TResult
  return handler(contextNote)
}
